import { SlotProps, SxProps, Theme, TypographyProps } from '@mui/material';
import { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import MuiListItemAvatar from '@mui/material/ListItemAvatar';
import MuiListItemIcon from '@mui/material/ListItemIcon';
import MuiListItemText, {
    ListItemTextOwnerState,
    ListItemTextProps
} from '@mui/material/ListItemText';
import React from 'react';

import { Avatar, AvatarProps } from '../avatar';
import { FlexBox } from '../box';
import { Checkbox } from '../checkbox';
import {
    CircularProgress,
    CircularProgressProps,
    CircularProgressWithLabel
} from '../circular-progress';
import type { FacilityInfoDtoLike } from '../facility-picker';
import { FacilityBadge } from '../facility-picker/badge';
import { getIcon, IconProps, IconType } from '../icon';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { deepMerge } from '../utils';

type CheckboxState = NonNullable<MuiCheckboxProps['checked']> | 'indeterminate';

type TextSlotProps = SlotProps<
    React.ElementType<TypographyProps>,
    unknown,
    ListItemTextOwnerState
>;

interface ItemContentProps {
    primary?: React.ReactNode;
    primaryRight?: React.ReactNode;
    secondary?: React.ReactNode;
    secondaryRight?: React.ReactNode;
    textSlotProps?: ListItemTextProps['slotProps'] & {
        primaryRight?: TextSlotProps;
        secondaryRight?: TextSlotProps;
    };
}

interface ItemBaseProps {
    className?: string;
    'data-role'?: string;
    disabled?: boolean;
    divider?: 'top' | 'bottom';
    loading?: boolean;
    selected?: boolean;
    sx?: SxProps<Theme>;
}

interface ItemAvatarSlot {
    avatar: AvatarProps;
    iconProps?: IconProps;
    icon?: never;
    progress?: never;
    checkbox?: never;
    facilityBadge?: never;
}

interface ItemIconSlot {
    icon: IconType;
    iconProps?: IconProps;
    avatar?: never;
    progress?: never;
    checkbox?: never;
    facilityBadge?: never;
}

interface ItemProgressSlot {
    progress: CircularProgressProps;
    avatar?: never;
    icon?: never;
    iconProps?: never;
    checkbox?: never;
    facilityBadge?: never;
}

interface ItemCheckboxSlot {
    checkbox: CheckboxState;
    avatar?: never;
    icon?: never;
    iconProps?: never;
    progress?: never;
    facilityBadge?: never;
}

interface ItemFacilityBadgeSlot {
    facilityBadge:
        | {
              onFetch?: never;
              moreText: (amount: number) => string;
              facilityInfo: FacilityInfoDtoLike;
          }
        | {
              onFetch: () => Promise<FacilityInfoDtoLike | undefined>;
              moreText: (amount: number) => string;
              label: React.ReactNode;
          };
    avatar?: never;
    icon?: never;
    iconProps?: never;
    progress?: never;
    checkbox?: never;
}

interface ItemNoSlot {
    avatar?: never;
    icon?: never;
    iconProps?: never;
    progress?: never;
    checkbox?: never;
    facilityBadge?: never;
}

type ItemLeftSlotProps =
    | ItemAvatarSlot
    | ItemIconSlot
    | ItemProgressSlot
    | ItemCheckboxSlot
    | ItemFacilityBadgeSlot
    | ItemNoSlot;

export type ItemProps = ItemBaseProps & ItemContentProps & ItemLeftSlotProps;

const isPrimitive = (value: unknown): value is string | number => {
    return typeof value === 'string' || typeof value === 'number';
};

const ItemWrapper = styled(FlexBox)(({ theme }) => ({
    '.MuiListItemSecondaryAction-root': { right: theme.spacing(1) },
    '.MuiListItemAvatar-root, .MuiListItemIcon-root': {
        boxSizing: 'content-box',
        minWidth: 32,
        paddingRight: theme.spacing(2)
    }
}));

export const Item = React.forwardRef<HTMLLIElement, ItemProps>((props, ref) => {
    const {
        avatar,
        checkbox,
        icon,
        iconProps,
        primary,
        primaryRight,
        secondary,
        secondaryRight,
        selected,
        progress,
        facilityBadge,
        loading,
        className,
        sx,
        textSlotProps
    } = props;

    const mergedTextSlotProps = deepMerge<
        Exclude<ItemProps['textSlotProps'], undefined>
    >({ primary: {} }, textSlotProps ?? {});

    const Icon = icon ? getIcon(icon) : () => null;

    const leftSlot = (() => {
        if (loading) {
            return (
                <MuiListItemIcon>
                    <Skeleton variant="circular" width={40} height={40} />
                </MuiListItemIcon>
            );
        }

        if (facilityBadge) {
            return (
                <MuiListItemIcon>
                    {facilityBadge.onFetch ? (
                        <FacilityBadge.Async
                            label={facilityBadge.label}
                            onFetch={facilityBadge.onFetch}
                            moreText={facilityBadge.moreText}
                        />
                    ) : (
                        <FacilityBadge
                            facilityInfo={facilityBadge.facilityInfo}
                            moreText={facilityBadge.moreText}
                        />
                    )}
                </MuiListItemIcon>
            );
        }

        if (progress) {
            if (progress.variant === 'indeterminate') {
                return (
                    <MuiListItemIcon>
                        <CircularProgress color="primary" {...progress} />
                    </MuiListItemIcon>
                );
            }
            if (progress.value !== undefined) {
                return (
                    <MuiListItemIcon>
                        <CircularProgressWithLabel
                            color="success"
                            {...progress}
                        />
                    </MuiListItemIcon>
                );
            }
            console.warn(
                'progress value is required in the determinated variant of CircularProgress component.'
            );
        }

        if (checkbox !== undefined) {
            return (
                <MuiListItemIcon>
                    <Checkbox
                        edge="start"
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        indeterminate={checkbox === 'indeterminate'}
                    />
                </MuiListItemIcon>
            );
        }

        if (avatar) {
            return (
                <MuiListItemAvatar>
                    {iconProps ? (
                        <Avatar {...avatar} title={undefined}>
                            <Icon {...iconProps} />
                        </Avatar>
                    ) : (
                        <Avatar {...avatar} />
                    )}
                </MuiListItemAvatar>
            );
        }

        if (icon) {
            return (
                <MuiListItemIcon>
                    <Icon {...iconProps} />
                </MuiListItemIcon>
            );
        }

        return null;
    })();

    return (
        <ItemWrapper
            stretch
            overflow="hidden"
            vAlign="center"
            ref={ref}
            sx={sx}
            className={className}
        >
            {leftSlot}
            <FlexBox
                hAlign="space-between"
                gap={2}
                stretch
                vAlign="center"
                overflow="hidden"
            >
                <MuiListItemText
                    disableTypography={!isPrimitive(primary)}
                    primary={
                        loading ? (
                            <Skeleton variant="text" width="60%" />
                        ) : (
                            primary
                        )
                    }
                    secondary={
                        loading ? (
                            <Skeleton variant="text" width="40%" />
                        ) : (
                            secondary
                        )
                    }
                    slotProps={mergedTextSlotProps}
                />
                {(primaryRight || secondaryRight) && (
                    <MuiListItemText
                        disableTypography={!isPrimitive(primaryRight)}
                        primary={primaryRight}
                        secondary={secondaryRight}
                        slotProps={{
                            primary: {
                                ...mergedTextSlotProps.primary,
                                ref: null,
                                ...mergedTextSlotProps.primaryRight,
                                align: 'right'
                            },
                            secondary: {
                                ...mergedTextSlotProps.secondary,
                                ref: null,
                                ...mergedTextSlotProps.secondaryRight,
                                align: 'right'
                            }
                        }}
                    />
                )}
            </FlexBox>
        </ItemWrapper>
    );
});

(Item as React.FunctionComponent).displayName = 'Item';
